import { useMemo, useState } from 'react'
import { toast } from 'sonner'

import { can } from '@/features/auth/permissions'
import { BoardView } from '@/features/board/BoardView'
import { useSession } from '@/features/auth/use-session'
import { ProjectForm } from '@/features/projects/components/ProjectForm'
import { useStageSetsOfKind } from '@/features/projects/api'
import { useMoveProject } from '@/features/projects/reorder'
import { usePatchProjectRow } from '@/features/projects/row-edit'
import { PageHeader } from '@/shared/components/common'
import {
  EmptyState,
  ErrorState,
  NoResultsState,
  SkeletonCards,
  SkeletonRows,
} from '@/shared/components/data'
import { Button } from '@/shared/components/ui'
import { toStageSet } from '@/shared/api/adapt'
import { useT } from '@/shared/i18n'

import { usePortfolioKpis, usePortfolioProjects } from './api'
import { KpiRow } from './components/KpiRow'
import { PortfolioCards } from './components/PortfolioCards'
import { PortfolioFiltersBar } from './components/PortfolioFilters'
import { PortfolioTable, type PortfolioRowEditing } from './components/PortfolioTable'
import { PARAM, type PortfolioScope, usePortfolioFilters } from './use-portfolio-filters'

/**
 * `/portfolio` — la pantalla insignia.
 *
 * Todos los proyectos vivos de todas las empresas, con el semaforo adelante del
 * nombre. Los filtros salen de la URL para que un link de Slack muestre
 * exactamente lo que estaba viendo quien lo mando.
 *
 * **Tabla, tarjetas y tablero son la misma lista.** El tablero vivia en `/board`
 * con su propia query y sin un solo filtro: no habia forma de ver el tablero de
 * una empresa ni el de una persona, y dos vistas del mismo portafolio podian
 * mostrar conjuntos distintos. Consolidarlas deja una sola fuente y un solo
 * juego de filtros; el agrupamiento por fase pasa a ser lo que siempre fue, una
 * forma de mirar, no otra pantalla.
 */
export function PortfolioPage() {
  const t = useT()
  const controls = usePortfolioFilters()
  const { filters, queryFilters, isFiltered, clearFilters } = controls

  const { me } = useSession()

  /**
   * El alcance efectivo: lo que dice la URL o, si no dice nada, lo que
   * corresponde al rol.
   *
   * Un `tech_member` entra a lo suyo; un `admin` entra al total, que es la vista
   * para la que existe el portafolio. El default NO se escribe en la URL: si se
   * escribiera, un link compartido llevaria puesto el alcance de quien lo mando.
   */
  const scope: PortfolioScope = filters.scope ?? (me?.role === 'tech_member' ? 'mio' : 'todo')

  /**
   * En "solo lo mio", el responsable es quien esta mirando.
   *
   * Se sustituye aca —sobre los filtros ya traducidos— y no dentro del hook de
   * query: `toQueryFilters` es una funcion pura sobre la URL y no sabe ni puede
   * saber quien tiene la sesion abierta.
   */
  const scopedFilters = useMemo(
    () => (scope === 'mio' && me ? { ...queryFilters, [PARAM.owner]: [me.id] } : queryFilters),
    [scope, me, queryFilters],
  )

  // Los KPIs reciben los MISMOS filtros que la lista (empresa y responsable):
  // la cabecera y la tabla tienen que contestar la misma pregunta.
  const kpis = usePortfolioKpis(scopedFilters)
  // El tablero necesita los culminados: tiene una columna para ellos y el
  // default del endpoint es no traerlos.
  const projects = usePortfolioProjects(scopedFilters, { needsDone: filters.view === 'tablero' })
  const [creating, setCreating] = useState(false)
  const move = useMoveProject()
  const patchRow = usePatchProjectRow()
  const stageSets = useStageSetsOfKind('project')

  /**
   * La tabla se edita en la fila. Las fases que se ofrecen son las del set que
   * contiene la fase actual del proyecto —el clon de SU empresa—, no las
   * aplanadas del sistema: esas tienen otros ids y el backend las rechaza.
   */
  const editing = useMemo<PortfolioRowEditing | undefined>(() => {
    if (!can(me, 'project:write')) return undefined
    const sets = (stageSets.data ?? []).map(toStageSet)
    return {
      stagesFor: (project) =>
        sets.find((set) => set.stages.some((stage) => stage.id === project.stage.id))?.stages ?? [],
      onPatch: (project, patch) => patchRow.mutate({ id: project.id, patch }),
    }
    // `mutate` es estable; depender de `patchRow` entero rehace esto en cada estado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me, stageSets.data, patchRow.mutate])

  const items = projects.data ?? []

  return (
    <div className="flex flex-col">
      <PageHeader
        title={t('portfolio.title')}
        description={t(scope === 'mio' ? 'portfolio.descriptionMine' : 'portfolio.description')}
        actions={
          <span className="flex items-center gap-3">
            {/*
              El contador de la lista y el KPI "Proyectos activos" dan números
              distintos y están a treinta centímetros uno del otro: el KPI
              excluye culminados y pausados, la lista no. Los dos son correctos
              y nada lo decía (QA funcional 1.7). El `title` lo dice — el KPI ya
              declaraba el suyo ("Ni culminados ni pausados").
            */}
            {projects.data ? (
              <span className="num text-2xs text-text-3" title={t('portfolio.projectCountHint')}>
                {t('portfolio.projectCount', { count: items.length })}
              </span>
            ) : null}
            {can(me, 'project:create') ? (
              <Button variant="primary" onClick={() => setCreating(true)}>
                {t('projects.new')}
              </Button>
            ) : null}
          </span>
        }
      />

      {/* La empresa del filtro llega preseleccionada: si estoy mirando UNA sola
          empresa, el proyecto nuevo casi siempre es de esa. Con varias, no se
          adivina. */}
      <ProjectForm
        open={creating}
        onClose={() => setCreating(false)}
        companyId={filters.companies.length === 1 ? filters.companies[0] : undefined}
      />

      {/*
        Los KPIs van ARRIBA de los filtros y la barra de filtros es la que se
        pega: los cuatro numeros se leen una vez al entrar, mientras que los
        filtros se tocan mientras se recorre la lista.
      */}
      <div className="p-4 pb-0">
        <KpiRow
          kpis={kpis.data}
          isPending={kpis.isPending}
          error={kpis.error}
          onRetry={() => void kpis.refetch()}
          companyId={filters.companies.length === 1 ? (filters.companies[0] ?? null) : null}
        />
      </div>

      <PortfolioFiltersBar {...controls} scope={scope} className="mt-4" />

      <div className="flex flex-col gap-4 p-4">
        {/*
          El tablero maneja sus propios estados de carga y de vacio: tiene que
          poder decir "esta columna no tiene nada" con el resto del tablero
          dibujado, que es distinto de "no hay proyectos". Por eso se ramifica
          antes y no comparte el arbol de abajo.
        */}
        {filters.view === 'tablero' ? (
          items.length === 0 && isFiltered && !projects.isPending ? (
            <NoResultsState onClearFilters={clearFilters} />
          ) : (
            <BoardView
              projects={items}
              isPending={projects.isPending}
              isError={projects.isError}
              error={projects.error}
              onRetry={() => void projects.refetch()}
            />
          )
        ) : projects.isPending ? (
          filters.view === 'tarjetas' ? (
            <SkeletonCards count={6} />
          ) : (
            <SkeletonRows rows={10} />
          )
        ) : projects.isError ? (
          <ErrorState error={projects.error} onRetry={() => void projects.refetch()} />
        ) : items.length === 0 && isFiltered ? (
          <NoResultsState onClearFilters={clearFilters} />
        ) : items.length === 0 ? (
          <EmptyState
            title={t('portfolio.empty.title')}
            description={t('portfolio.empty.description')}
          />
        ) : filters.view === 'tarjetas' ? (
          <PortfolioCards projects={items} />
        ) : (
          <PortfolioTable
            projects={items}
            // Reordenar desde acá, también filtrado: cada movimiento se pega a
            // una fila visible y no salta por encima de lo que el filtro oculta.
            onReorder={
              can(me, 'project:reorder')
                ? (id, anchor) => move.mutate({ id, beforeId: null, afterId: null, anchor })
                : undefined
            }
            onRejectedDrop={() => toast.info(t('priorities.ongoing.rejectReason'))}
            editing={editing}
          />
        )}
      </div>
    </div>
  )
}
